import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["row", "counter", "trainingProgress", "trainingBar"]

  declare readonly rowTargets: HTMLElement[]
  declare readonly counterTarget: HTMLElement
  declare readonly hasCounterTarget: boolean
  declare readonly trainingProgressTarget: HTMLElement
  declare readonly hasTrainingProgressTarget: boolean
  declare readonly trainingBarTarget: HTMLElement
  declare readonly hasTrainingBarTarget: boolean

  async confirm(event: Event) {
    const button = event.currentTarget as HTMLButtonElement
    const row = button.closest("[data-ai-review-target='row']") as HTMLElement
    const transactionId = row.dataset.transactionId
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content

    button.disabled = true
    button.textContent = "Confirming..."

    try {
      const response = await fetch(`/transactions/${transactionId}/confirm_ai_prediction`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
      })

      if (response.ok) {
        // Mark as confirmed visually
        row.classList.add("bg-green-50")
        row.querySelector("[data-ai-badge]")?.classList.replace("bg-purple-100", "bg-green-100")
        row.querySelector("[data-ai-badge]")?.classList.replace("text-purple-700", "text-green-700")

        // Remove action buttons
        const actionsContainer = row.querySelector("[data-actions]")
        if (actionsContainer) {
          actionsContainer.innerHTML = '<span class="text-sm text-green-600 font-medium">Confirmed</span>'
        }

        this.updateCounter(-1)
      } else {
        button.disabled = false
        button.textContent = "Confirm"
        const data = await response.json()
        alert(data.error || "Failed to confirm prediction")
      }
    } catch (error) {
      button.disabled = false
      button.textContent = "Confirm"
      alert("Failed to confirm prediction")
    }
  }

  async reject(event: Event) {
    const button = event.currentTarget as HTMLButtonElement
    const row = button.closest("[data-ai-review-target='row']") as HTMLElement
    const transactionId = row.dataset.transactionId
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content

    button.disabled = true
    button.textContent = "Removing..."

    try {
      const response = await fetch(`/transactions/${transactionId}/reject_ai_prediction`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
      })

      if (response.ok) {
        // Remove the row with animation
        row.style.transition = "opacity 0.3s ease-out"
        row.style.opacity = "0"
        setTimeout(() => row.remove(), 300)

        this.updateCounter(-1)
      } else {
        button.disabled = false
        button.textContent = "Reject"
        const data = await response.json()
        alert(data.error || "Failed to reject prediction")
      }
    } catch (error) {
      button.disabled = false
      button.textContent = "Reject"
      alert("Failed to reject prediction")
    }
  }

  updateCounter(delta: number) {
    if (this.hasCounterTarget) {
      const current = parseInt(this.counterTarget.textContent || "0") || 0
      const newCount = Math.max(0, current + delta)
      this.counterTarget.textContent = String(newCount)

      if (newCount === 0) {
        // Hide the badge when count reaches 0
        this.counterTarget.closest("[data-review-badge]")?.classList.add("hidden")
      }
    }
  }

  updateTrainingProgress(progress: { current: number; required: number }) {
    if (this.hasTrainingProgressTarget) {
      this.trainingProgressTarget.textContent = `${progress.current}/${progress.required}`
    }
    if (this.hasTrainingBarTarget) {
      const percentage = Math.min(100, (progress.current / progress.required) * 100)
      this.trainingBarTarget.style.width = `${percentage}%`
    }
  }
}
